import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  AppBar,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Switch,
  TextField,
  Toolbar,
  Typography,
  useTheme,
} from '@mui/material';
import {
  AdminPanelSettingsOutlined,
  DarkModeOutlined,
  HelpOutlineOutlined,
  LightModeOutlined,
  Logout,
  Menu as MenuIcon,
} from '@mui/icons-material';

import { useAuthStore } from '../stores/auth.store';
import { useOrganizationStore } from '../stores/organization.store';
import { OrganizationSchema } from '../types/organization.type';
import { useAuthPermissions } from '../hooks/useAuthPermissions';
import { NAV_CATALOG, isNavGroup, isVerifierOnlyUser, type NavItemDef } from '../auth';
import { activeNavPath, navIcon } from '../constants/adminIcons';
import { ContentThemeProvider } from '../theme/ContentThemeProvider';
import { Sidebar } from './Sidebar';

const DRAWER_WIDTH = 280;
const COLLAPSED_DRAWER_WIDTH = 76;
const SIDEBAR_COLLAPSED_STORAGE_KEY = 'admin.sidebarCollapsed';
const ORG_SEARCH_DEBOUNCE_MS = 300;
/** Label of the section holding the catalog's top-level (ungrouped) entries. */
const PLATFORM_SECTION = 'Platform';

interface NavSection {
  label: string;
  items: NavItemDef[];
}

interface LayoutProps {
  children: React.ReactNode;
  onThemeToggle: () => void;
}

function readSidebarCollapsed(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

/** Flat red brand mark: the admin shield on a solid accent square. */
const BrandMark: React.FC = () => {
  const theme = useTheme();
  return (
    <Box
      aria-hidden
      sx={{
        width: 36,
        height: 36,
        flexShrink: 0,
        borderRadius: '10px',
        display: 'grid',
        placeItems: 'center',
        bgcolor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
      }}
    >
      <AdminPanelSettingsOutlined sx={{ fontSize: 22 }} />
    </Box>
  );
};

const Layout: React.FC<LayoutProps> = ({ children, onThemeToggle }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const { user, logout, accessibleOrgIds, isPlatformAdmin: platformAdmin, permVerStale, tokenPayload } =
    useAuthStore();
  const { organizations, getOrganizations, selectedOrganization, setSelectedOrganization } =
    useOrganizationStore();
  const { isReadOnly, filterMenuEntries } = useAuthPermissions();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(readSidebarCollapsed);
  const [profileAnchor, setProfileAnchor] = useState<HTMLElement | null>(null);
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({ [PLATFORM_SECTION]: true });
  const [search, setSearch] = useState('');

  const verifierOnly = isVerifierOnlyUser(tokenPayload);
  const effectiveDrawerWidth = sidebarCollapsed ? COLLAPSED_DRAWER_WIDTH : DRAWER_WIDTH;
  const contentTransition = theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  });

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, String(sidebarCollapsed));
    } catch {
      /* storage unavailable: the rail simply resets next visit */
    }
  }, [sidebarCollapsed]);

  /** Catalog → sidebar sections: top-level entries first, then one section per group. */
  const sections = useMemo<NavSection[]>(() => {
    const visible = filterMenuEntries(NAV_CATALOG);
    const platform: NavItemDef[] = [];
    const groups: NavSection[] = [];
    for (const entry of visible) {
      if (isNavGroup(entry)) {
        if (entry.subItems.length > 0) groups.push({ label: entry.text, items: entry.subItems });
      } else {
        platform.push(entry);
      }
    }
    return platform.length > 0 ? [{ label: PLATFORM_SECTION, items: platform }, ...groups] : groups;
  }, [filterMenuEntries]);

  const activePath = useMemo(
    () => activeNavPath(location.pathname, sections.flatMap((section) => section.items.map((item) => item.path))),
    [location.pathname, sections],
  );

  // Open the section holding the current page whenever the route changes, then
  // bring its entry into view once the section has expanded.
  useEffect(() => {
    const active = sections.find((section) => section.items.some((item) => item.path === activePath));
    if (active) setOpenSections((prev) => (prev[active.label] ? prev : { ...prev, [active.label]: true }));
    const handle = window.setTimeout(() => {
      document
        .querySelectorAll<HTMLElement>('nav [aria-current="page"]')
        .forEach((el) => el.scrollIntoView({ block: 'nearest' }));
    }, theme.transitions.duration.standard + 50);
    return () => window.clearTimeout(handle);
  }, [activePath, sections, theme.transitions.duration.standard]);

  const orgOptions = useMemo(() => {
    if (platformAdmin || accessibleOrgIds.length === 0) return organizations;
    const allowed = new Set(accessibleOrgIds);
    return organizations.filter((o) => allowed.has(o.id));
  }, [organizations, accessibleOrgIds, platformAdmin]);

  // The store restores the saved organization at startup; drop it if this user cannot access it.
  useEffect(() => {
    if (!selectedOrganization) return;
    if (platformAdmin || accessibleOrgIds.length === 0 || accessibleOrgIds.includes(selectedOrganization.id)) return;
    setSelectedOrganization(null);
    sessionStorage.removeItem('selectedOrganization');
  }, [selectedOrganization, platformAdmin, accessibleOrgIds, setSelectedOrganization]);

  useEffect(() => {
    if (selectedOrganization || orgOptions.length !== 1) return;
    const only = orgOptions[0];
    setSelectedOrganization(only);
    sessionStorage.setItem('selectedOrganization', JSON.stringify(only));
  }, [orgOptions, selectedOrganization, setSelectedOrganization]);

  // Debounced: one request after typing pauses, not one per keystroke.
  useEffect(() => {
    if (verifierOnly) return;
    const handle = window.setTimeout(() => {
      getOrganizations({ page: 1, limit: 10, name: search || undefined });
    }, search ? ORG_SEARCH_DEBOUNCE_MS : 0);
    return () => window.clearTimeout(handle);
  }, [search, verifierOnly, getOrganizations]);

  const handleNavigate = useCallback(
    (path: string) => {
      navigate(path);
      setMobileOpen(false);
    },
    [navigate],
  );

  const toggleSection = useCallback(
    (label: string) => setOpenSections((prev) => ({ ...prev, [label]: !prev[label] })),
    [],
  );

  const roleChips = (
    <>
      {platformAdmin && <Chip label="Platform admin" color="primary" size="small" sx={{ fontWeight: 600 }} />}
      {isReadOnly && <Chip label="Read-only" color="warning" size="small" variant="outlined" sx={{ fontWeight: 600 }} />}
    </>
  );

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Sidebar.Header collapsedChildren={<BrandMark />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <BrandMark />
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, lineHeight: 1.2 }} noWrap>
              Recyclee Admin
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap component="p">
              {verifierOnly ? 'Verification portal' : 'Administration'}
            </Typography>
          </Box>
        </Box>
      </Sidebar.Header>

      {!verifierOnly && (
        <Sidebar.ExpandedOnly>
          <Divider />
          <Box sx={{ px: 2, py: 2 }}>
            <Autocomplete
              size="small"
              value={selectedOrganization}
              options={orgOptions}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              getOptionLabel={(o) => o.name}
              onChange={(_, v: OrganizationSchema | null) => {
                if (!v) return;
                setSelectedOrganization(v);
                sessionStorage.setItem('selectedOrganization', JSON.stringify(v));
              }}
              onInputChange={(_, v, reason) => {
                if (reason === 'input' || reason === 'clear') setSearch(v);
              }}
              renderInput={(params) => <TextField {...params} label="Organization" />}
              fullWidth
            />
          </Box>
          <Divider />
        </Sidebar.ExpandedOnly>
      )}

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
        <Sidebar.Nav>
          {sections.map((section) => (
            <Sidebar.NavGroup
              key={section.label}
              label={section.label}
              open={Boolean(openSections[section.label])}
              onToggle={() => toggleSection(section.label)}
            >
              {section.items.map((item) => {
                const Icon = navIcon(item.path);
                return (
                  <Sidebar.NavItem
                    key={item.path}
                    icon={<Icon />}
                    label={item.text}
                    selected={item.path === activePath}
                    onClick={() => handleNavigate(item.path)}
                  />
                );
              })}
            </Sidebar.NavGroup>
          ))}
        </Sidebar.Nav>
      </Box>

      <Sidebar.ExpandedOnly>
        <Divider />
      </Sidebar.ExpandedOnly>
      <Sidebar.Nav>
        <Sidebar.NavItem
          icon={<HelpOutlineOutlined />}
          label="Help"
          selected={location.pathname.startsWith('/knowledge-base')}
          onClick={() => handleNavigate('/knowledge-base')}
        />
        <Sidebar.NavItem
          icon={
            <Avatar sx={{ width: 28, height: 28, fontSize: '0.875rem', bgcolor: 'text.primary', color: 'background.paper' }}>
              {user?.username?.[0]?.toUpperCase() ?? '?'}
            </Avatar>
          }
          label={user?.username ?? 'Account'}
          selected={Boolean(profileAnchor)}
          onClick={(event) => setProfileAnchor(event.currentTarget)}
          buttonProps={{ 'aria-haspopup': 'menu', 'aria-label': 'Account menu' }}
        />
      </Sidebar.Nav>
      <Sidebar.CollapseToggle onClick={() => setSidebarCollapsed((prev) => !prev)} />
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          display: { xs: 'block', sm: 'none' },
          bgcolor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          boxShadow: 'none',
        }}
      >
        <Toolbar sx={{ gap: 1.5 }}>
          <IconButton
            edge="start"
            aria-label="Open navigation"
            onClick={() => setMobileOpen(true)}
            sx={{ color: theme.palette.primary.main }}
          >
            <MenuIcon />
          </IconButton>
          <Typography sx={{ fontWeight: 700 }} noWrap>
            Recyclee Admin
          </Typography>
        </Toolbar>
      </AppBar>

      <Sidebar
        width={DRAWER_WIDTH}
        collapsedWidth={COLLAPSED_DRAWER_WIDTH}
        collapsed={sidebarCollapsed}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
      >
        {drawer}
      </Sidebar>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { sm: `calc(100% - ${effectiveDrawerWidth}px)` },
          minWidth: 0,
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: theme.palette.background.paper,
          transition: { sm: contentTransition },
        }}
      >
        <Toolbar sx={{ display: { xs: 'flex', sm: 'none' } }} />
        <Box
          sx={{
            flexGrow: 1,
            p: 3,
            bgcolor: theme.palette.background.default,
            borderRadius: '16px',
            mt: { xs: 1.5, sm: 2 },
            mr: { xs: 1.5, sm: 2 },
            mb: { xs: 1.5, sm: 2 },
            ml: { xs: 1.5, sm: 0 },
            minWidth: 0,
          }}
        >
          <ContentThemeProvider>
            {permVerStale && (
              <Alert
                severity="warning"
                sx={{ mb: 3 }}
                action={
                  <Button color="inherit" size="small" onClick={() => logout()}>
                    Log out
                  </Button>
                }
              >
                Your permissions have changed. Log out and sign in again for the updates to take effect.
              </Alert>
            )}
            {children}
          </ContentThemeProvider>
        </Box>
      </Box>

      <Menu
        anchorEl={profileAnchor}
        open={Boolean(profileAnchor)}
        onClose={() => setProfileAnchor(null)}
        anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
        transformOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        marginThreshold={8}
        PaperProps={{ sx: { borderRadius: '12px', minWidth: 240, boxShadow: theme.shadows[3] } }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap>
            {user?.username}
          </Typography>
          {selectedOrganization && (
            <Typography variant="caption" color="text.secondary" noWrap component="p">
              {selectedOrganization.name}
            </Typography>
          )}
          {(platformAdmin || isReadOnly) && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>{roleChips}</Box>
          )}
        </Box>
        <Divider />
        <MenuItem onClick={onThemeToggle}>
          <ListItemIcon>
            {theme.palette.mode === 'dark' ? <DarkModeOutlined fontSize="small" /> : <LightModeOutlined fontSize="small" />}
          </ListItemIcon>
          <ListItemText primary="Dark mode" />
          <Switch
            edge="end"
            size="small"
            checked={theme.palette.mode === 'dark'}
            onClick={(event) => event.stopPropagation()}
            onChange={onThemeToggle}
            inputProps={{ 'aria-label': 'Dark mode' }}
          />
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            setProfileAnchor(null);
            logout();
          }}
        >
          <ListItemIcon>
            <Logout fontSize="small" sx={{ color: theme.palette.error.main }} />
          </ListItemIcon>
          Log out
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default Layout;
